import type {
  AgentChatSessionAssetsContract,
  AgentChatSessionContract,
  AgentChatSessionPageContract,
  AgentChatSessionSummaryContract,
  SyncAgentChatSessionRequest,
} from '@nest-react/domain';
import { apiRequest } from '~/app/providers';
import { ApiRequestError } from '~/app/regionalApiClient';

import {
  notifyAgentChatSessionCreated,
  notifyAgentChatSessionDeleted,
  notifyAgentChatSessionUpdated,
  sessionContractToSummary,
} from './agentChatSessionListEvents';

const SESSION_LIST_CACHE_TTL_MS = 10 * 60 * 1000;
export const AGENT_HOME_RECENT_SESSION_LIMIT = 8;
export const AGENT_CHAT_HISTORY_LIMIT = 20;
const sessionListCache = new Map<
  string,
  {
    expiresAt: number;
    request: Promise<AgentChatSessionPageContract>;
  }
>();

const invalidateSessionListCache = (workspaceId: string): void => {
  for (const key of sessionListCache.keys()) {
    if (key.startsWith(`${workspaceId}:`)) {
      sessionListCache.delete(key);
    }
  }
};

const cloneSessionListResponse = ({
  items,
  nextCursor,
}: AgentChatSessionPageContract): AgentChatSessionPageContract => ({
  items: items.map(session => ({ ...session })),
  nextCursor,
});

export const listAgentChatSessions = async (
  workspaceId: string,
  limit = AGENT_HOME_RECENT_SESSION_LIMIT,
  cursor?: string
): Promise<AgentChatSessionPageContract> => {
  const safeLimit = Math.min(
    Math.max(Math.trunc(limit), 1),
    AGENT_CHAT_HISTORY_LIMIT
  );
  const cacheKey = `${workspaceId}:${safeLimit}:${cursor ?? 'start'}`;
  const cached = sessionListCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.request;

  const params = new URLSearchParams({ limit: String(safeLimit) });
  if (cursor) params.set('cursor', cursor);
  const request = apiRequest<AgentChatSessionPageContract>(
    `/workspaces/${workspaceId}/agent/sessions?${params.toString()}`
  ).then(cloneSessionListResponse);
  sessionListCache.set(cacheKey, {
    expiresAt: Date.now() + SESSION_LIST_CACHE_TTL_MS,
    request,
  });
  void request.catch(() => {
    if (sessionListCache.get(cacheKey)?.request === request) {
      sessionListCache.delete(cacheKey);
    }
  });
  return request;
};

export const createAgentChatSession = async (
  workspaceId: string,
  title?: string
): Promise<AgentChatSessionContract> => {
  const session = await apiRequest<AgentChatSessionContract>(
    `/workspaces/${workspaceId}/agent/sessions`,
    {
      method: 'POST',
      body: JSON.stringify({ title }),
    }
  );
  invalidateSessionListCache(workspaceId);
  notifyAgentChatSessionCreated(workspaceId, sessionContractToSummary(session));
  return session;
};

export const getAgentChatSession = async (
  workspaceId: string,
  sessionId: string
): Promise<AgentChatSessionContract> => {
  const session = await apiRequest<AgentChatSessionContract>(
    `/workspaces/${workspaceId}/agent/sessions/${sessionId}`
  );
  invalidateSessionListCache(workspaceId);
  notifyAgentChatSessionUpdated(
    workspaceId,
    sessionContractToSummary(session),
    { previewMode: 'preserve' }
  );
  return session;
};

export const getAgentChatSessionSummary = async (
  workspaceId: string,
  sessionId: string
): Promise<AgentChatSessionSummaryContract> =>
  apiRequest<AgentChatSessionSummaryContract>(
    `/workspaces/${workspaceId}/agent/sessions/${sessionId}/summary`
  );

const previewRefreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
const previewRefreshGeneration = new Map<string, number>();
const PREVIEW_REFRESH_DELAY_MS = 1500;

export const scheduleAgentChatSessionPreviewRefresh = (
  workspaceId: string,
  sessionId: string,
  delayMs = PREVIEW_REFRESH_DELAY_MS
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const key = `${workspaceId}:${sessionId}`;
  const existing = previewRefreshTimers.get(key);
  if (existing) {
    clearTimeout(existing);
  }
  previewRefreshTimers.set(
    key,
    setTimeout(() => {
      previewRefreshTimers.delete(key);
      void refreshAgentChatSessionPreview(workspaceId, sessionId);
    }, delayMs)
  );
};

export const cancelAgentChatSessionPreviewRefresh = (
  workspaceId: string,
  sessionId: string
): void => {
  const key = `${workspaceId}:${sessionId}`;
  const existing = previewRefreshTimers.get(key);
  if (existing) {
    clearTimeout(existing);
    previewRefreshTimers.delete(key);
  }
  previewRefreshGeneration.set(
    key,
    (previewRefreshGeneration.get(key) ?? 0) + 1
  );
};

export const refreshAgentChatSessionPreview = async (
  workspaceId: string,
  sessionId: string
): Promise<void> => {
  const key = `${workspaceId}:${sessionId}`;
  const generation = (previewRefreshGeneration.get(key) ?? 0) + 1;
  previewRefreshGeneration.set(key, generation);
  try {
    const summary = await getAgentChatSessionSummary(workspaceId, sessionId);
    if (previewRefreshGeneration.get(key) !== generation) {
      return;
    }
    notifyAgentChatSessionUpdated(workspaceId, summary, {
      previewMode: 'replace',
    });
  } catch {
    // Preview refresh should not block chat flows.
  }
};

export const listAgentChatSessionAssets = async (
  workspaceId: string,
  sessionId: string
): Promise<AgentChatSessionAssetsContract> =>
  apiRequest<AgentChatSessionAssetsContract>(
    `/workspaces/${workspaceId}/agent/sessions/${sessionId}/assets`
  );

export const syncAgentChatSession = async (
  workspaceId: string,
  sessionId: string,
  payload: SyncAgentChatSessionRequest
): Promise<AgentChatSessionContract> => {
  const session = await apiRequest<AgentChatSessionContract>(
    `/workspaces/${workspaceId}/agent/sessions/${sessionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  );
  notifyAgentChatSessionUpdated(
    workspaceId,
    sessionContractToSummary(session),
    { previewMode: 'preserve' }
  );
  scheduleAgentChatSessionPreviewRefresh(workspaceId, sessionId);
  return session;
};

export const renameAgentChatSession = async (
  workspaceId: string,
  sessionId: string,
  title: string
): Promise<AgentChatSessionSummaryContract> => {
  const session = await apiRequest<AgentChatSessionSummaryContract>(
    `/workspaces/${workspaceId}/agent/sessions/${sessionId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }
  );
  invalidateSessionListCache(workspaceId);
  notifyAgentChatSessionUpdated(workspaceId, session, {
    previewMode: 'replace',
  });
  return session;
};

export const deleteAgentChatSession = async (
  workspaceId: string,
  sessionId: string
): Promise<void> => {
  try {
    await apiRequest<void>(
      `/workspaces/${workspaceId}/agent/sessions/${sessionId}`,
      {
        method: 'DELETE',
      }
    );
  } catch (error) {
    if (!(error instanceof ApiRequestError) || error.status !== 404) {
      throw error;
    }
  } finally {
    invalidateSessionListCache(workspaceId);
    notifyAgentChatSessionDeleted(workspaceId, sessionId);
  }
};
